import { ReactNode } from "react";
import { Card, OverflowText, Button, cn, CopyIdButton } from "ui";

/**
 * Card Header:
 *  - either image or icon
 *  - either status badge or plain text
 *  - card title
 *  - ID copy (optional if id exist)
 *
 * Card Body:
 *  - Free JSX
 *
 * Card Footer:
 *  - one primary and one secondary
 *  - one primary and list of other actions
 */
type EntityCardProps = {
  imageDimensions: 56 | 72;
  title: string;
  id?: string;
  children: ReactNode;
  primaryAction?: ReactNode;
  secondaryAction?: ReactNode;
  menu?: ReactNode;
  variant?: "default" | "info";
  // otherActionsList?: never;
} & (
  | {
      imageUrl: string;
      icon?: never;
    }
  | {
      icon: JSX.Element;
      imageUrl?: never;
    }
) &
  (
    | {
        badge: ReactNode;
        subTitle?: never;
      }
    | {
        subTitle: string;
        badge?: never;
      }
  );

const imageDimensionsClasses = {
  56: "w-[56px] h-[56px]",
  72: "w-[72px] h-[72px]",
};

const variants = {
  default: "bg-foundations-subtlest",
  info: "bg-info-subtler",
};

export default function EntityCard({
  imageDimensions = 56,
  imageUrl,
  icon,
  subTitle,
  badge,
  title,
  id,
  children,
  primaryAction,
  secondaryAction,
  menu,
  variant = "default",
}: EntityCardProps) {
  return (
    <Card className="group flex flex-col pb-3 focus-within:border-blue-500">
      {/* Card Header */}
      <div className="mb-4 flex items-center gap-4">
        <div className="block shrink-0 grow-0">
          {imageUrl ? (
            <img
              className="rounded-sm"
              width={imageDimensions}
              height={imageDimensions}
              src={imageUrl}
              alt={title}
            />
          ) : (
            <div
              className={cn(
                "flex items-center justify-center rounded-md  text-foundations-boldest",
                imageDimensionsClasses[imageDimensions],
                variants[variant]
              )}
            >
              {icon}
            </div>
          )}
        </div>

        <div className="flex h-full flex-col items-start justify-center gap-1 overflow-hidden font-medium">
          {subTitle ? (
            <p className="whitespace-nowrap text-[11px] uppercase leading-[14px] tracking-wider text-foundations-subtler">
              {subTitle}
            </p>
          ) : (
            badge
          )}

          <OverflowText>
            <div className="provider-name w-full text-lg leading-6 text-foundations-bold">
              {title}
            </div>
          </OverflowText>

          {id && <CopyIdButton id={id} />}
        </div>
      </div>

      {primaryAction || secondaryAction ? (
        <div className="relative">
          {/* Card Body */}
          <div className="-mx-4 block border-t border-t-foundations-subtle px-4 py-3 pb-0 transition-all duration-300  ">
            {children}
          </div>

          {/* Card Footer */}

          <div className="-mx-4 flex flex-row-reverse flex-wrap items-center justify-between gap-4 border-t border-t-foundations-subtle bg-foundations-default px-4 pt-3 transition-all duration-300 md:invisible md:absolute md:inset-0 md:opacity-0 md:group-focus-within:visible md:group-focus-within:opacity-100 md:group-hover:visible md:group-hover:opacity-100">
            {primaryAction}

            {secondaryAction ? secondaryAction : <>{menu}</>}
          </div>
        </div>
      ) : (
        <div className="-mx-4 block px-4 pb-3 transition-all duration-300 ">
          {children}
        </div>
      )}
    </Card>
  );
}

EntityCard.PrimaryButton = function PrimaryButton({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      size="xs"
      className={cn(
        "flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap px-3 py-[6px] text-sm font-medium",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
};

EntityCard.SecondaryButton = function SecondaryButton({
  children,
  className,
  ...props
}: React.ComponentProps<typeof Button>) {
  return (
    <Button
      size="xs"
      variant="secondary"
      className={cn(
        "flex flex-1 cursor-pointer items-center justify-center whitespace-nowrap px-3 py-[6px] text-sm font-medium",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
};
